import { BanUserSchema, type BanUserRequest } from '@aysar/contracts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { useToast } from '@/shared/hooks/useToast/useToast';

import { useBanAccountMutation } from '@/shared/services/account-status/account-status.api';

import { buildBanFields } from '../config/account-actions.config';

/**
 * Banning an account — terminal, and the reason is mandatory because the member
 * is shown it.
 *
 * Same shape as suspension and for the same reason: the api requires a reason,
 * so this is a form. The dialog says out loud that a ban cannot be undone from
 * here, because the only other lever — `restore` — deliberately does not apply
 * to a ban (N9), and an operator who assumed otherwise would find that out too
 * late.
 */
export function BanUserModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('uiKit');
  const toast = useToast();
  const [banUser] = useBanAccountMutation();

  const userId = typeof modalData?.['userId'] === 'string' ? modalData['userId'] : null;
  const email = typeof modalData?.['email'] === 'string' ? modalData['email'] : '';
  const fields = useMemo(() => buildBanFields(t), [t]);

  async function submit(values: BanUserRequest): Promise<void> {
    if (userId === null) {
      return;
    }

    await banUser({ id: userId, body: values }).unwrap();
    toast.success(t('account.ban.success'));
    onCancel();
  }

  return (
    <DynamicFormModal<BanUserRequest>
      isOpen={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
      title={t('account.ban.title', { email })}
      description={t('account.ban.description')}
      fields={fields}
      schema={BanUserSchema}
      submitLabel={t('account.ban.submit')}
      onSubmit={submit}
    />
  );
}
